import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Settings, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "mdm-cookie-consent";

const defaultConsent = {
  necessary: true, // always true, cannot be disabled
  statistics: false,
  marketing: false,
  preferences: false,
  timestamp: null,
  version: 1,
};

function applyConsent(consent) {
  if (consent.statistics && typeof window.__initPosthogAnalytics === "function") {
    window.__initPosthogAnalytics();
  }
  // Google Consent Mode v2 — update granular consent signals
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
      analytics_storage: consent.statistics ? "granted" : "denied",
      functionality_storage: consent.preferences ? "granted" : "denied",
      personalization_storage: consent.preferences ? "granted" : "denied",
    });
  }
  // Meta Pixel — grant/revoke based on marketing consent
  if (typeof window.fbq === "function") {
    window.fbq("consent", consent.marketing ? "grant" : "revoke");
    if (consent.marketing) {
      window.fbq("track", "PageView");
    }
  }
}

export default function CookieConsent() {
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choices, setChoices] = useState(defaultConsent);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!stored) {
        setShow(true);
      } else {
        // Apply already-saved consent immediately
        applyConsent(stored);
      }
    } catch (e) {
      setShow(true);
    }
  }, []);

  const save = (consent) => {
    const payload = {
      ...consent,
      necessary: true,
      timestamp: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    applyConsent(payload);
    setShow(false);
    setShowSettings(false);
  };

  const acceptAll = () =>
    save({
      necessary: true,
      statistics: true,
      marketing: true,
      preferences: true,
    });

  const rejectAll = () =>
    save({
      necessary: true,
      statistics: false,
      marketing: false,
      preferences: false,
    });

  const saveChoices = () => save(choices);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[90] p-3 sm:p-4"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-[1100px] mx-auto bg-[#0B192C] text-[#F9F8F6] border border-[#0B192C] shadow-[0_20px_60px_-15px_rgba(11,25,44,0.5)]">
            {!showSettings ? (
              <div className="p-5 md:p-7 grid md:grid-cols-12 gap-5 items-center">
                <div className="md:col-span-7 flex gap-4">
                  <Cookie size={26} strokeWidth={1.3} className="text-[#E89B6C] flex-shrink-0 mt-1" />
                  <div>
                    <p className="overline text-[#E89B6C]">Informativa Cookie</p>
                    <p className="font-serif-display text-xl mt-1 leading-tight">
                      Utilizziamo i cookie per migliorare la tua esperienza.
                    </p>
                    <p className="mt-2 text-xs font-light text-[#F9F8F6]/75 leading-relaxed">
                      Usiamo cookie tecnici (sempre attivi) e, con il tuo consenso, cookie
                      di statistica e preferenze. Puoi accettare tutto, rifiutare o
                      personalizzare. Maggiori informazioni nella{" "}
                      <Link to="/cookie" className="underline hover:text-[#E89B6C]">
                        Cookie Policy
                      </Link>{" "}
                      e{" "}
                      <Link to="/privacy" className="underline hover:text-[#E89B6C]">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </div>
                </div>
                <div className="md:col-span-5 flex flex-col sm:flex-row md:justify-end gap-2">
                  <button
                    onClick={rejectAll}
                    data-testid="cookie-reject-all"
                    className="px-5 py-3 text-xs uppercase tracking-[0.18em] border border-[#F9F8F6]/30 hover:border-[#F9F8F6] transition-colors"
                  >
                    Rifiuta tutto
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    data-testid="cookie-customize"
                    className="px-5 py-3 text-xs uppercase tracking-[0.18em] border border-[#F9F8F6]/30 hover:border-[#F9F8F6] transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Settings size={12} />
                    Personalizza
                  </button>
                  <button
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                    className="px-5 py-3 text-xs uppercase tracking-[0.18em] bg-[#E89B6C] text-[#0B192C] hover:bg-[#F9F8F6] transition-colors"
                  >
                    Accetta tutto
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 md:p-7 max-h-[80vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <p className="overline text-[#E89B6C]">Preferenze Cookie</p>
                    <p className="font-serif-display text-2xl mt-1">
                      Personalizza le tue scelte
                    </p>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    aria-label="Chiudi preferenze"
                    data-testid="cookie-settings-close"
                    className="text-[#F9F8F6]/60 hover:text-[#F9F8F6]"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="space-y-3">
                  <ConsentRow
                    title="Cookie necessari"
                    desc="Indispensabili per il funzionamento del sito (sessione, sicurezza, preferenze cookie). Non possono essere disattivati."
                    checked
                    disabled
                  />
                  <ConsentRow
                    title="Statistiche e analisi"
                    desc="PostHog: ci aiuta a capire come usi il sito tramite dati aggregati e anonimi. Trasferimento extra-UE (USA) con clausole standard."
                    checked={choices.statistics}
                    onChange={(v) =>
                      setChoices({ ...choices, statistics: v })
                    }
                    testid="cookie-statistics-toggle"
                  />
                  <ConsentRow
                    title="Preferenze"
                    desc="Salvano le tue preferenze di lettura (es. ultima sezione visualizzata)."
                    checked={choices.preferences}
                    onChange={(v) =>
                      setChoices({ ...choices, preferences: v })
                    }
                    testid="cookie-preferences-toggle"
                  />
                  <ConsentRow
                    title="Marketing e pubblicità"
                    desc="Google Ads / Google Tag — usati per misurare le conversioni dalle nostre campagne pubblicitarie. Senza il tuo consenso, gli eventi non vengono inviati a Google."
                    checked={choices.marketing}
                    onChange={(v) =>
                      setChoices({ ...choices, marketing: v })
                    }
                    testid="cookie-marketing-toggle"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mt-6">
                  <button
                    onClick={rejectAll}
                    data-testid="cookie-settings-reject-all"
                    className="px-5 py-3 text-xs uppercase tracking-[0.18em] border border-[#F9F8F6]/30 hover:border-[#F9F8F6] transition-colors flex-1"
                  >
                    Rifiuta tutto
                  </button>
                  <button
                    onClick={saveChoices}
                    data-testid="cookie-settings-save"
                    className="px-5 py-3 text-xs uppercase tracking-[0.18em] bg-[#E89B6C] text-[#0B192C] hover:bg-[#F9F8F6] transition-colors flex-1"
                  >
                    Salva preferenze
                  </button>
                  <button
                    onClick={acceptAll}
                    data-testid="cookie-settings-accept-all"
                    className="px-5 py-3 text-xs uppercase tracking-[0.18em] border border-[#F9F8F6]/30 hover:border-[#F9F8F6] transition-colors flex-1"
                  >
                    Accetta tutto
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ConsentRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <div className="border border-[#F9F8F6]/15 p-4 flex items-start gap-4">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        disabled={disabled}
        data-testid={testid}
        onClick={() => onChange && onChange(!checked)}
        className={`mt-1 relative inline-flex h-5 w-9 flex-shrink-0 transition-colors ${
          checked ? "bg-[#E89B6C]" : "bg-[#F9F8F6]/20"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 bg-[#F9F8F6] transition-transform ${
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </button>
      <div className="flex-1">
        <p className="font-serif-display text-base text-[#F9F8F6]">{title}</p>
        <p className="text-xs font-light text-[#F9F8F6]/70 mt-1 leading-relaxed">
          {desc}
        </p>
      </div>
    </div>
  );
}

// Public API: re-open preferences from anywhere (e.g., footer link)
export function openCookiePreferences() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  } catch (e) {}
}
